import { isORPCErrorStatus, ORPCError, type ORPCErrorCode, type ORPCErrorJSON } from "@orpc/client";
import { clsx, type ClassValue } from "clsx";
import Decimal from "decimal.js";
import { useMemo, type JSX } from "react";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { CONSTANTS } from "./constants";
import { ADMIN_ENV } from "./env";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isInArray<T, const A extends readonly T[]>(item: T, array: A): item is A[number] {
  return array.includes(item as A[number]);
}

export function formatCurrency(
  amount: number | undefined | null,
  options?: Intl.NumberFormatOptions,
  stringOnly?: false,
): string | JSX.Element;
export function formatCurrency(
  amount: number | undefined | null,
  options: Intl.NumberFormatOptions | undefined,
  stringOnly: true,
): string;
export function formatCurrency(
  amount: number | undefined | null,
  options?: Intl.NumberFormatOptions,
  stringOnly?: boolean,
): string | JSX.Element {
  if (amount === undefined || amount === null) {
    return "N/A";
  }

  const value = new Intl.NumberFormat(CONSTANTS.LANGUAGE === "ar" ? "ar-EG" : "en-US", {
    numberingSystem: "latn",
    style: "currency",
    currency: "EGP",
    maximumFractionDigits: 0,
    ...options,
  }).format(new Decimal(amount).div(100).toNumber());

  if (stringOnly) {
    return value;
  }

  return <span className="tabular-nums">{value}</span>;
}

export function formatPercentage(percentage: number | undefined | null) {
  if (percentage === undefined || percentage === null) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 0,
  }).format(percentage);
}

export function stringToRandomNumber(input: string, min: number = 0, max: number = 10, float: boolean = false) {
  let hash = 0x811c9dc5;

  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  hash >>>= 0;

  const mixed = (Math.imul(hash, 1664525) + 1013904223) >>> 0;

  const range = max - min;

  if (float) {
    const ratio = mixed / 0xffffffff;
    return min + ratio * range;
  }

  const intVal = mixed % (range + 1);
  return min + intVal;
}

export function getImageUrl(imageKey?: string): string | undefined {
  if (!imageKey) return undefined;
  return `${ADMIN_ENV.VITE_PUBLIC_R2_ENDPOINT}/${imageKey}`;
}

type RecursivelyReplaceNullWithUndefined<T> = T extends null
  ? undefined
  : T extends Date
    ? T
    : {
        [K in keyof T]: T[K] extends (infer U)[]
          ? RecursivelyReplaceNullWithUndefined<U>[]
          : RecursivelyReplaceNullWithUndefined<T[K]>;
      };

export function nullsToUndefined<T>(obj: T): RecursivelyReplaceNullWithUndefined<T> {
  if (obj === null) {
    return undefined as any;
  }

  if (obj && obj.constructor.name === "Object") {
    for (let key in obj) {
      obj[key] = nullsToUndefined(obj[key]) as any;
    }
  }
  return obj as any;
}

export function useActiveFilters(obj: Record<string, any>, { excludeKeys }: { excludeKeys?: string[] } = {}) {
  return useMemo(
    () =>
      Object.entries(obj).some(([key, value]) => {
        if (isInArray(key, ["sortBy", "sortOrder", "page", "limit", ...(excludeKeys ?? [])])) return false;
        if (Array.isArray(value)) return value.length > 0;
        return value !== null && value !== undefined && value !== "";
      }),
    [obj, excludeKeys],
  );
}

export function isDefinedError(error: unknown): error is ORPCError<any, any> {
  return isORPCErrorJson(errorToPlainObject(error));
}

function errorToPlainObject(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    const plain = Object.create(null);
    Object.getOwnPropertyNames(error).forEach((key) => {
      if (key !== "stack") (plain as any)[key] = (error as any)[key];
    });
    return plain;
  }

  if (typeof error === "object" && error !== null) {
    return error as Record<string, unknown>;
  }

  return { message: String(error) };
}

function isObject(value: unknown): value is Record<PropertyKey, unknown> {
  if (!value || typeof value !== "object") {
    return false;
  }

  const proto = Object.getPrototypeOf(value);

  return proto === Object.prototype || !proto || !proto.constructor;
}

export function isORPCErrorJson(json: unknown): json is ORPCErrorJSON<ORPCErrorCode, unknown> {
  if (!isObject(json)) {
    return false;
  }

  const validKeys = ["defined", "code", "status", "message", "data"];
  if (Object.keys(json).some((k) => !validKeys.includes(k))) {
    return false;
  }

  return (
    "defined" in json &&
    typeof json.defined === "boolean" &&
    "code" in json &&
    typeof json.code === "string" &&
    "status" in json &&
    typeof json.status === "number" &&
    isORPCErrorStatus(json.status) &&
    "message" in json &&
    typeof json.message === "string"
  );
}

/**
 * Filters the keys of object `a` to only those that also exist in object `b`.
 */
export function filterKeys<A extends Record<string, any>, B extends Record<string, any>>(a: A, b: B) {
  type K = keyof A & keyof B;
  const result = {} as Pick<A, K>;
  for (const key in a) {
    if (key in b) {
      const k = key as K;
      result[k] = a[k];
    }
  }
  return result;
}

/**
 * React hook that memoizes the result of filterKeys, only recalculating when the values
 * for the keys that are returned from filterKeys change (not on whole object identity change).
 */
export function useFilterKeys<A extends Record<string, any>, B extends Record<string, any>>(
  a: A,
  b: B,
): Pick<A, keyof A & keyof B> {
  return filterKeys(a, b) as Pick<A, keyof A & keyof B>;
}

type MaskKeys<A, B> = {
  [K in keyof A]: K extends keyof B ? undefined : A[K];
};

export function removeFilters<A extends object, B extends object>(a: A, b: B): MaskKeys<A, B> {
  const result: any = {};
  for (const key in a) {
    if (key in b) {
      result[key] = undefined;
    } else {
      result[key] = a[key];
    }
  }
  return result;
}

export function getNestedObjValue(obj: unknown, path: string[]) {
  return path.reduce((acc: any, key) => {
    if (acc === null || acc === undefined) return undefined;
    return acc[key];
  }, obj);
}

export const withDefaults = <TShape extends z.ZodRawShape, TDefaults extends Partial<z.infer<z.ZodObject<TShape>>>>(
  schema: z.ZodObject<TShape>,
  defaults: TDefaults,
) => {
  const shape = schema.shape;

  return z.object(
    Object.fromEntries(
      (Object.keys(defaults) as Array<keyof TDefaults>).map((key) => [
        key,
        (shape[key as keyof TShape] as any)?.default(defaults[key] as never),
      ]),
    ) as {
      [K in keyof TDefaults & keyof TShape]: z.ZodDefault<TShape[K]>;
    },
  );
};
